import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { useDeleteProductMutation, useFetchAllProductsQuery } from "src/ui/containers/dashboard/products/api";
import { makeStyles } from "tss-react/mui";

import { fetchAllGroups } from "@admin/api/group";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { EmteriaRoles } from "@data/roles";
import { ConfirmDialog } from "@devices/components/shared";
import { Box, DialogContentText, TableRow, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { LoadingBox } from "@shared/Loading";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { fuzzyFilter } from "@shared/table/Tanstack.utils";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import {
    flexRender,
    getCoreRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import BuildConfigDialog from "./components/BuildConfigDialog";
import { EditProductDialog } from "./components/EditProductDialog";
import { tableColumns } from "./AdminProductsPage.utils";

const useStyles = makeStyles()((theme) => ({
    noDataBox: { display: "flex", justifyContent: "center", alignItems: "center", marginTop: theme.spacing(15), flex: 1 },
    card: { marginBottom: theme.spacing(5) },
    cardContent: { "&.MuiCardContent-root": { paddingTop: 0 } },
    cardTitleBox: { display: "flex", gap: theme.spacing(2), alignItems: "flex-end" },
    cell: { width: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    flex: { display: "flex", gap: theme.spacing(1) },
    searchContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2),
        gap: theme.spacing(2),
    },
}));

const AdminProductsPage = () => {
    const [query, setQuery] = React.useState("");

    const [deleteId, setDeleteId] = React.useState<number | null>(null);
    const [editProductId, setEditProductId] = React.useState<number | null>(null);
    const [configProduct, setConfigProduct] = React.useState<API.Product | null>(null);

    const [deleteProduct, { isLoading: isDeleting }] = useDeleteProductMutation();
    const { data: products = [], isLoading: isLoadingProducts } = useFetchAllProductsQuery();

    const editProduct = products.find((item) => item.id === editProductId);

    const workspaceId = useSelector(selectCurrentWorkspaceId);

    const { classes } = useStyles();
    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const { t } = useTranslation(["modals", "common", "pages"]);

    const table = useReactTable({
        data: products.map((item) => ({
            id: item.id,
            workspaceId: item.workspaceId,
            groupId: item.groupId,
            name: item.customBuildConfig?.productName ?? t("pages:adminProductsPage.unknownProductName"),
            codename: item.codename,
            creationDate: item.created,
            builds: item.numberOfBuilds,
            androidVersion: item.androidVersion,
            visibility: { product: item },
            actions: {
                product: item,
                isDeleting: isDeleting,
                onDelete: setDeleteId,
                setConfigProduct,
                setEditProductId,
            },
        })),
        columns: tableColumns,
        filterFns: { fuzzy: fuzzyFilter },
        state: { globalFilter: query },
        defaultColumn: { size: 0 },
        autoResetPageIndex: false,
        initialState: {
            sorting: [{ id: "name", desc: false }],
            pagination: {
                pageIndex: 0,
                pageSize: 12,
            },
        },
        onGlobalFilterChange: setQuery,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });
    const tableRows = table.getPaginationRowModel().rows;

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [
        table.getState().pagination.pageIndex,
        table.getState().pagination.pageSize,
        tableRows.length,
    ]);

    const onDelete = async () => {
        if (deleteId && workspaceId !== undefined && workspaceId !== null) {
            const result = await deleteProduct(deleteId);
            if (!("error" in result)) {
                showSuccessToast(t("pages:adminProductsPage.toast.productDeletedSuccess"));
            }
            setDeleteId(null);
        }
    };
    const onSearch = async (userEmail: string) => {
        if (!userEmail) {
            return;
        }

        try {
            await fetchAllGroups({ userEmail });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };

    const onClearSearch = () => {
        setQuery("");
    };

    const handleResetFilter = () => {
        setQuery("");
        table.setPageIndex(0);
    };

    if (isLoadingProducts) {
        return <LoadingBox />;
    }

    if (!products.length && !isLoadingProducts) {
        return (
            <Box className={classes.noDataBox}>
                <Typography>{t("pages:adminProductsPage.noProducts")}</Typography>
            </Box>
        );
    }

    return (
        <PageContainer fitViewport>
            <div className={classes.searchContainer}>
                <QueryBuilderField
                    placeholder={t("common:button.search")}
                    name="ownerEmail"
                    value={query}
                    onChange={setQuery}
                    onSubmit={onSearch}
                    onClear={onClearSearch}
                />
            </div>
            <Box className={layoutClasses.tableLayout}>
                <Box className={layoutClasses.tableWrapper}>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody
                                table={table}
                                filter={{ isActive: !!query.length, onResetFilter: handleResetFilter }}
                                noDataMessage={t("pages:adminProductsPage.noProducts")}
                            >
                                {tableRows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) =>
                                            flexRender(cell.column.columnDef.cell, {
                                                ...cell.getContext(),
                                                key: cell.id,
                                            }),
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                </Box>

                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    rowsPerPageOptions={[12, 24, 48]}
                    count={table.getFilteredRowModel().rows.length}
                    rowsPerPage={table.getState().pagination.pageSize}
                    page={table.getState().pagination.pageIndex}
                    onPageChange={(event, newPage) => table.setPageIndex(newPage)}
                    onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
                />
            </Box>

            <ConfirmDialog
                title={t("modals:adminDeleteProduct.title")}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={!!deleteId}
                onConfirm={onDelete}
                onClose={() => setDeleteId(null)}
            >
                <DialogContentText>{t("modals:adminDeleteProduct.description")}</DialogContentText>
            </ConfirmDialog>
            {editProduct ? (
                <EditProductDialog
                    open
                    title={t("modals:adminEditProduct.title")}
                    product={editProduct}
                    onClose={() => setEditProductId(null)}
                />
            ) : null}
            <BuildConfigDialog product={configProduct} open={!!configProduct} onClose={() => setConfigProduct(null)} />
        </PageContainer>
    );
};
export default withRequiredRole(
    withMetaDescription(AdminProductsPage, {
        title: i18next.t("pages:adminProductsPage.metaTitle"),
        description: i18next.t("pages:adminProductsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaDeveloperRole],
);
